import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Crown, Loader2, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { GuaranteeRefundDialog } from "@/components/billing/guarantee-status-card";
import {
  V3_STATE_LABELS,
  V3_STATUS_QUERY_KEY,
  confirmV3SavedCard,
  formatCentsAsDollars,
  startV3CardSetup,
  type V3BillingStatusV3,
} from "@/hooks/use-v3-billing";

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

/**
 * "Your plan" on the billing page for v3 per-property accounts: state,
 * billable counts, the monthly preview, trial end, lifetime allowance, and the
 * card / cancel / refund actions. The refund button exists only while the
 * server says the account is eligible (annual or lifetime within 30 days) —
 * monthly plans cancel anytime instead.
 */
export function V3PlanCard({ status }: { status: V3BillingStatusV3 }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [cancelEndsAt, setCancelEndsAt] = useState<string | null>(null);

  const refreshBilling = () => {
    queryClient.invalidateQueries({ queryKey: V3_STATUS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["/api/app/billing/summary"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
  };

  const savedCardMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => confirmV3SavedCard(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: V3_STATUS_QUERY_KEY });
      toast({ title: "Card confirmed", description: "We'll use your saved card when your plan bills." });
    },
    onError: (err) => {
      toast({
        title: "Couldn't confirm your card",
        description: parseErrorMessage(err, "Please try again, or add a new card."),
        variant: "destructive",
      });
    },
  });

  const cardMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => startV3CardSetup("/app/account/billing"),
    onError: (err) => {
      toast({
        title: "Couldn't open the card form",
        description: parseErrorMessage(err, "Please try again in a moment."),
        variant: "destructive",
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/billing/v3/cancel", {});
      return (await res.json()) as { ok: boolean; message: string; endsAt: string | null };
    },
    onSuccess: (data) => {
      setCancelOpen(false);
      setCancelEndsAt(data.endsAt ?? "pending");
      toast({ title: "Subscription canceled", description: data.message });
      refreshBilling();
    },
    onError: (err) => {
      toast({
        title: "Couldn't cancel",
        description: parseErrorMessage(err, "Please try again or contact support@padpulse.io."),
        variant: "destructive",
      });
    },
  });

  const keepMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/app/billing/v3/keep", {});
    },
    onSuccess: () => {
      setCancelEndsAt(null);
      toast({ title: "Your plan continues", description: "Nothing changes on your next bill." });
      refreshBilling();
    },
    onError: (err) => {
      toast({
        title: "Couldn't keep your plan",
        description: parseErrorMessage(err, "Please try again or contact support@padpulse.io."),
        variant: "destructive",
      });
    },
  });

  const trialEnds = formatDate(status.trialEndsAt);
  const refund = status.refund;
  const isMonthly = status.interval === "monthly";
  const canCancel = isMonthly && (status.state === "active" || status.state === "trialing" || status.state === "past_due");
  const cancelEndsLabel = cancelEndsAt && cancelEndsAt !== "pending" ? formatDate(cancelEndsAt) : null;

  return (
    <Card data-testid="card-v3-plan">
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
          Your plan
          <Badge variant="outline" data-testid="badge-v3-state">
            {V3_STATE_LABELS[status.state] ?? status.state}
          </Badge>
          {status.lifetime && (
            <Badge className="bg-brand-subtle text-brand border-brand/30" variant="outline" data-testid="badge-v3-plan-lifetime">
              <Crown className="mr-1 h-3 w-3" aria-hidden="true" />
              {status.lifetime.label}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Active properties</dt>
            <dd className="text-lg font-semibold" data-testid="text-v3-active-properties">{status.counts.activeProperties}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Owners</dt>
            <dd className="text-lg font-semibold" data-testid="text-v3-active-owners">{status.counts.activeOwners}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Billable locks</dt>
            <dd className="text-lg font-semibold" data-testid="text-v3-billable-locks">{status.counts.billableLocks}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{status.lifetime ? "Extra each month" : "Monthly total"}</dt>
            <dd className="text-lg font-semibold" data-testid="text-v3-monthly-preview">
              {formatCentsAsDollars(status.monthlyPreviewCents)}
              <span className="text-xs font-normal text-muted-foreground">/mo</span>
            </dd>
          </div>
        </dl>

        {status.lifetime && (
          <p className="text-sm text-muted-foreground" data-testid="text-v3-lifetime-allowance">
            Your {String(status.lifetime.label ?? "").toLowerCase()} plan covers up to {status.lifetime.allowance} properties.
            {status.lifetime.needsOverageConsent ? " Properties above that need billing confirmed." : ""}
          </p>
        )}
        {status.state === "trialing" && trialEnds && (
          <p className="text-sm text-muted-foreground" data-testid="text-v3-trial-ends">
            Your free trial ends {trialEnds}.{status.hasCard
              ? " Your card will be charged then."
              : status.savedCard
                ? ` Confirm your saved card${status.savedCard.last4 ? ` ending ${status.savedCard.last4}` : ""} or add a new one to keep everything running. Nothing is charged unless you confirm.`
                : " Add a card to keep everything running."}
          </p>
        )}
        {status.readOnly && (
          <p className="text-sm text-red-700 dark:text-red-300" data-testid="text-v3-read-only">
            Your account is read-only until billing is fixed. You can still view everything.
          </p>
        )}
        {isMonthly && (
          <p className="text-xs text-muted-foreground" data-testid="text-v3-monthly-cancel-copy">
            {status.monthlyCancelCopy}
          </p>
        )}
        {cancelEndsAt && (
          <p className="text-sm text-muted-foreground" data-testid="text-v3-cancel-pending">
            Your plan is set to end{cancelEndsLabel ? ` on ${cancelEndsLabel}` : " at the end of this billing period"}.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {status.savedCard && !status.hasCard && (
            <Button
              size="sm"
              className="bg-brand hover:bg-brand-hover text-white"
              onClick={() => savedCardMutation.mutate()}
              disabled={savedCardMutation.isPending}
              data-testid="button-v3-confirm-saved-card"
            >
              {savedCardMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1.5 h-4 w-4" />}
              {status.savedCard.last4 ? `Keep card ending ${status.savedCard.last4}` : "Keep my saved card"}
            </Button>
          )}
          {status.state !== "comped" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => cardMutation.mutate()}
              disabled={cardMutation.isPending}
              data-testid="button-v3-card"
            >
              {cardMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1.5 h-4 w-4" />}
              {status.hasCard ? "Update card" : "Add a card"}
            </Button>
          )}
          {canCancel && !cancelEndsAt && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => setCancelOpen(true)}
              data-testid="button-v3-cancel"
            >
              Cancel plan
            </Button>
          )}
          {cancelEndsAt && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => keepMutation.mutate()}
              disabled={keepMutation.isPending}
              data-testid="button-v3-keep"
            >
              {keepMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Keep my plan
            </Button>
          )}
          {refund.eligible && (
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => setRefundOpen(true)}
              data-testid="button-v3-refund"
            >
              <ShieldCheck className="mr-1.5 h-4 w-4" />
              Request my refund
            </Button>
          )}
        </div>
        {refund.eligible && (
          <p className="text-xs text-muted-foreground" data-testid="text-v3-refund-window">
            30-day money-back guarantee on your {refund.kind === "lifetime" ? "lifetime purchase" : "annual payment"}, until {formatDate(refund.until)}.
          </p>
        )}
      </CardContent>

      <Dialog
        open={cancelOpen}
        onOpenChange={(next) => {
          if (cancelMutation.isPending) return;
          setCancelOpen(next);
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="dialog-v3-cancel"
          onInteractOutside={(e) => {
            if (cancelMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (cancelMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (cancelMutation.isPending) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Cancel your plan?</DialogTitle>
            <DialogDescription>{status.monthlyCancelCopy}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Everything keeps working until the end of this billing period. Your data stays safe.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelMutation.isPending}>
              Keep my plan
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              data-testid="button-v3-confirm-cancel"
            >
              {cancelMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Cancel plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {refund.eligible && (
        <GuaranteeRefundDialog open={refundOpen} onOpenChange={setRefundOpen} variant="v3" onRefunded={refreshBilling} />
      )}
    </Card>
  );
}
